import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type {
  SimulationTopology,
  LaneDef,
  FrameDef,
  NodeDef,
  ConnectionDef,
  BranchPredicate,
  ContextMap,
  NodeTypeStyleDef,
  ConnectionStyleDef,
} from '../../types/index.ts';
import { resolveNodeStyle, resolveConnectionStyle } from '../../services/styleResolver.ts';
import type { RootState } from '../index.ts';

export interface TopologyState {
  lanes: LaneDef[];
  branchLanes: LaneDef[];
  frames: FrameDef[];
  connections: ConnectionDef[];
  eventRouting: Record<string, string[]>;
  branchPredicates: Record<string, BranchPredicate>;
  adrLinks: Record<string, string>;
  contextMap: ContextMap | null;
  nodeTypeStyles: Record<string, NodeTypeStyleDef>;
  connectionStyles: Record<string, ConnectionStyleDef>;
  /** Maps scenario node IDs (n00-Name) to topology node IDs (moment-0-...::Name) */
  scenarioToTopologyNodeMap: Record<string, string>;
  /** Maps topology node IDs back to scenario node IDs */
  topologyToScenarioNodeMap: Record<string, string>;
  loaded: boolean;
}

const initialState: TopologyState = {
  lanes: [],
  branchLanes: [],
  frames: [],
  connections: [],
  eventRouting: {},
  branchPredicates: {},
  adrLinks: {},
  contextMap: null,
  nodeTypeStyles: {},
  connectionStyles: {},
  scenarioToTopologyNodeMap: {},
  topologyToScenarioNodeMap: {},
  loaded: false,
};

const topologySlice = createSlice({
  name: 'topology',
  initialState,
  reducers: {
    loadTopology(state, action: PayloadAction<SimulationTopology>) {
      const topo = action.payload;
      // Filter lanes: use isBranch (Moment M6) or group (legacy)
      state.lanes = topo.lanes.filter((l) =>
        l.isBranch !== undefined ? !l.isBranch : l.group === 'context',
      );
      state.branchLanes = topo.lanes.filter((l) =>
        l.isBranch !== undefined ? l.isBranch : l.group !== 'context',
      );
      state.frames = topo.frames;
      state.connections = topo.connections;
      state.eventRouting = topo.eventRouting;
      state.branchPredicates = topo.branchPredicates;
      state.adrLinks = topo.adrLinks ?? {};
      state.contextMap = topo.contextMap ?? null;
      state.nodeTypeStyles = topo.nodeTypeStyles ?? {};
      state.connectionStyles = topo.connectionStyles ?? {};

      // Build scenario→topology node ID map.
      // Topology IDs: "moment-0-Event-discovery::PublishEvent"
      // Scenario IDs: "n00-PublishEvent"
      // Match by label (the node name after the last :: or after nXX-)
      const nodeMap: Record<string, string> = {};
      for (const frame of topo.frames) {
        for (const node of frame.nodes) {
          // Use the node's display label to build the scenario ID pattern
          const topoLabel = node.label;
          // Build all possible scenario ID patterns that could match
          // Scenario format: n{momentIndex:2d}-{nodeName}
          const frameMatch = frame.id.match(/^moment-(\d+)/);
          if (frameMatch) {
            const momentIdx = parseInt(frameMatch[1], 10);
            const paddedIdx = String(momentIdx).padStart(2, '0');
            const scenarioNodeId = `n${paddedIdx}-${topoLabel}`;
            nodeMap[scenarioNodeId] = node.id;
          }
        }
      }
      state.scenarioToTopologyNodeMap = nodeMap;
      // Build reverse map
      const reverseMap: Record<string, string> = {};
      for (const [scenarioId, topoId] of Object.entries(nodeMap)) {
        reverseMap[topoId] = scenarioId;
      }
      state.topologyToScenarioNodeMap = reverseMap;
      state.loaded = true;
    },

    // ----- Live topology editing reducers -----

    updateNodeLabel(state, action: PayloadAction<{ nodeId: string; label: string }>) {
      const { nodeId, label } = action.payload;
      for (const frame of state.frames) {
        const node = frame.nodes.find((n) => n.id === nodeId);
        if (node) {
          node.label = label;
          break;
        }
      }
    },

    updateNodeDetail(state, action: PayloadAction<{ nodeId: string; detail: string }>) {
      const { nodeId, detail } = action.payload;
      for (const frame of state.frames) {
        const node = frame.nodes.find((n) => n.id === nodeId);
        if (node) {
          node.detail = detail;
          break;
        }
      }
    },

    addNodeToFrame(state, action: PayloadAction<{ frameId: string; node: NodeDef }>) {
      const { frameId, node } = action.payload;
      // Reject if node ID already exists anywhere in the topology
      const exists = state.frames.some((f) => f.nodes.some((n) => n.id === node.id));
      if (exists) return;
      const frame = state.frames.find((f) => f.id === frameId);
      if (frame) {
        frame.nodes.push(node);
      }
    },

    removeNode(state, action: PayloadAction<{ nodeId: string }>) {
      const { nodeId } = action.payload;
      for (const frame of state.frames) {
        const idx = frame.nodes.findIndex((n) => n.id === nodeId);
        if (idx >= 0) {
          frame.nodes.splice(idx, 1);
          break;
        }
      }
      // Clean up connections referencing this node
      state.connections = state.connections.filter((c) => c.from !== nodeId && c.to !== nodeId);
      // Clean up routing entries pointing to this node
      for (const [key, targets] of Object.entries(state.eventRouting)) {
        state.eventRouting[key] = targets.filter((t) => t !== nodeId);
      }
      // Clean up branch predicates
      delete state.branchPredicates[nodeId];
    },

    addConnection(state, action: PayloadAction<ConnectionDef>) {
      state.connections.push(action.payload);
    },

    removeConnection(state, action: PayloadAction<{ from: string; to: string }>) {
      const { from, to } = action.payload;
      state.connections = state.connections.filter((c) => !(c.from === from && c.to === to));
    },

    updateEventRouting(state, action: PayloadAction<{ eventType: string; targets: string[] }>) {
      const { eventType, targets } = action.payload;
      state.eventRouting[eventType] = targets;
    },

    removeEventRouting(state, action: PayloadAction<{ eventType: string }>) {
      delete state.eventRouting[action.payload.eventType];
    },
  },
});

export const {
  loadTopology,
  updateNodeLabel,
  updateNodeDetail,
  addNodeToFrame,
  removeNode,
  addConnection,
  removeConnection,
  updateEventRouting,
  removeEventRouting,
} = topologySlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectTopologyLoaded = (state: RootState) => state.topology.loaded;
export const selectLanes = (state: RootState) => state.topology.lanes;
export const selectBranchLanes = (state: RootState) => state.topology.branchLanes;
export const selectFrames = (state: RootState) => state.topology.frames;
export const selectConnections = (state: RootState) => state.topology.connections;
export const selectEventRouting = (state: RootState) => state.topology.eventRouting;
export const selectBranchPredicates = (state: RootState) => state.topology.branchPredicates;
export const selectAdrLinks = (state: RootState) => state.topology.adrLinks;

export const selectLaneById = (state: RootState, laneId: string): LaneDef | undefined => {
  return (
    state.topology.lanes.find((l) => l.id === laneId) ??
    state.topology.branchLanes.find((l) => l.id === laneId)
  );
};

/** Resolve a node ID that might be a scenario ID (n00-Name) to its topology ID. */
export const resolveNodeId = (state: RootState, nodeId: string): string => {
  return state.topology.scenarioToTopologyNodeMap[nodeId] ?? nodeId;
};

export const selectNodeById = (state: RootState, nodeId: string): NodeDef | undefined => {
  const resolvedId = resolveNodeId(state, nodeId);
  for (const frame of state.topology.frames) {
    const node = frame.nodes.find((n) => n.id === resolvedId);
    if (node) return node;
  }
  return undefined;
};

export const selectFrameByNodeId = (state: RootState, nodeId: string): FrameDef | undefined => {
  const resolvedId = resolveNodeId(state, nodeId);
  return state.topology.frames.find((f) => f.nodes.some((n) => n.id === resolvedId));
};

export const selectScenarioToTopologyMap = (state: RootState) =>
  state.topology.scenarioToTopologyNodeMap;

export const selectConnectionsForNode = (state: RootState, nodeId: string): ConnectionDef[] => {
  const resolved = resolveNodeId(state, nodeId);
  return state.topology.connections.filter((c) => c.from === resolved || c.to === resolved);
};

export const selectOutgoingConnections = (state: RootState, nodeId: string): ConnectionDef[] => {
  const resolved = resolveNodeId(state, nodeId);
  return state.topology.connections.filter((c) => c.from === resolved);
};

export const selectIncomingConnections = (state: RootState, nodeId: string): ConnectionDef[] => {
  const resolved = resolveNodeId(state, nodeId);
  return state.topology.connections.filter((c) => c.to === resolved);
};

export const selectBranchPredicate = (
  state: RootState,
  nodeId: string,
): BranchPredicate | undefined => {
  return state.topology.branchPredicates[nodeId];
};

export const selectEventTargetNodes = (state: RootState, eventType: string): string[] => {
  return state.topology.eventRouting[eventType] ?? [];
};

// ---------------------------------------------------------------------------
// Context Map Selectors
// ---------------------------------------------------------------------------

export const selectContextMap = (state: RootState) => state.topology.contextMap;

export const selectBoundedContexts = createSelector(
  [selectContextMap],
  (contextMap) => contextMap?.contexts ?? [],
);

export const selectContextRelationships = createSelector(
  [selectContextMap],
  (contextMap) => contextMap?.relationships ?? [],
);

export const selectContextForNode = (state: RootState, nodeId: string): string | null => {
  const node = selectNodeById(state, nodeId);
  if (!node) return null;
  const lane = state.topology.lanes.find((l) => l.id === node.lane);
  return lane?.contextId ?? null;
};

/** Group all topology nodes by their bounded context (lane contextId). */
export const selectNodesByContext = createSelector([selectFrames, selectLanes], (frames, lanes) => {
  const laneContextMap = new Map(lanes.map((l) => [l.id, l.contextId]));
  const grouped: Record<string, NodeDef[]> = {};
  for (const frame of frames) {
    for (const node of frame.nodes) {
      const ctxId = laneContextMap.get(node.lane);
      if (!ctxId) continue;
      if (!grouped[ctxId]) grouped[ctxId] = [];
      grouped[ctxId].push(node);
    }
  }
  return grouped;
});

// ---------------------------------------------------------------------------
// Style Selectors
// ---------------------------------------------------------------------------

export const selectNodeTypeStyles = (state: RootState) => state.topology.nodeTypeStyles;
export const selectConnectionStyles = (state: RootState) => state.topology.connectionStyles;

/** Legend entries derived from node types actually present in the topology, ordered by first frame appearance. */
export const selectLegendEntries = createSelector(
  [selectFrames, selectNodeTypeStyles],
  (frames, topologyStyles) => {
    const seen = new Set<string>();
    const entries: { type: string; style: NodeTypeStyleDef }[] = [];
    for (const frame of frames) {
      for (const node of frame.nodes) {
        if (!seen.has(node.type)) {
          seen.add(node.type);
          entries.push({
            type: node.type,
            style: resolveNodeStyle(node.type, topologyStyles),
          });
        }
      }
    }
    return entries;
  },
);

/** Connection style entries derived from connection styles actually used in the topology. */
export const selectConnectionLegendEntries = createSelector(
  [selectConnections, selectConnectionStyles],
  (connections, topologyStyles) => {
    const seen = new Set<string>();
    const entries: { style: string; def: ConnectionStyleDef; label: string }[] = [];
    for (const conn of connections) {
      if (!seen.has(conn.style)) {
        seen.add(conn.style);
        entries.push({
          style: conn.style,
          def: resolveConnectionStyle(conn.style, topologyStyles),
          label: conn.style.replace(/-/g, ' '),
        });
      }
    }
    return entries;
  },
);

const RELATIONSHIP_LABELS: Record<string, { abbrev: string; label: string }> = {
  'customer-supplier': { abbrev: 'C/S', label: 'Customer / Supplier' },
  partnership: { abbrev: 'P', label: 'Partnership' },
  conformist: { abbrev: 'CF', label: 'Conformist' },
  'anticorruption-layer': { abbrev: 'ACL', label: 'Anticorruption Layer' },
  'open-host-service': { abbrev: 'OHS', label: 'Open Host Service' },
  'published-language': { abbrev: 'PL', label: 'Published Language' },
  'separate-ways': { abbrev: 'SW', label: 'Separate Ways' },
};

/** DDD relationship entries derived from context relationships in the topology. */
export const selectRelationshipLegendEntries = createSelector(
  [selectContextRelationships],
  (relationships) => {
    const seen = new Set<string>();
    const entries: { type: string; abbrev: string; label: string }[] = [];
    for (const rel of relationships) {
      if (!seen.has(rel.relationshipType)) {
        seen.add(rel.relationshipType);
        const info = RELATIONSHIP_LABELS[rel.relationshipType];
        entries.push({
          type: rel.relationshipType,
          abbrev: info?.abbrev ?? rel.relationshipType,
          label: info?.label ?? rel.relationshipType,
        });
      }
    }
    return entries;
  },
);

export default topologySlice.reducer;
